/**
 * Drives the bulk offer landing page: shoppers tick products, the totals keep
 * up, and one button hands the order to EasySell.
 *
 * The page does not ask for the name, phone or address itself. EasySell's order
 * payload carries a `hash` and a `discount_key` built inside its minified
 * bundle, so a form of our own could not send the order anyway - it would have
 * to copy what the shopper typed into the app's fields and hope the app's next
 * update kept them where they were. Instead the button loads the cart with
 * whatever was ticked and opens the app's own form, which reads the cart and
 * takes it from there.
 */

/** @returns {string} The shop's root path, which is not always "/". */
const root = () => window.Shopify?.routes?.root ?? '/';

const wait = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

class LandingOffer extends HTMLElement {
  /** An emptying of the cart already under way, if one is. */
  #cartCleared = null;

  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    this.addEventListener('click', this.#onClick);

    this.#watchSubmit();

    this.querySelector('[data-landing-offer-track]')?.addEventListener('scroll', () => this.#syncArrows(), {
      passive: true,
    });

    // A dialog ignores clicks on its own backdrop, so the image is the only
    // thing inside it that should keep a click.
    this.#lightbox?.addEventListener('click', (event) => {
      if (event.target === this.#lightbox) this.#lightbox.close();
    });
    this.#syncArrows();

    this.#render();
  }

  get #rows() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('[data-landing-offer-item]')]);
  }

  /** @param {Event} event */
  #onChange = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    // Reaching for an option means the shopper wants that product, so the row
    // ticks itself rather than asking for a second click on the circle.
    if (target.matches('[data-landing-offer-variant]')) {
      const check = target.closest('[data-landing-offer-item]')?.querySelector('[data-landing-offer-check]');
      if (check instanceof HTMLInputElement && !check.checked) check.checked = true;
    }

    if (target.matches('[data-landing-offer-check], [data-landing-offer-variant], [data-landing-offer-qty]')) {
      this.#render();
    }
  };

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = /** @type {HTMLElement} */ (event.target);

    const zoom = target.closest('[data-landing-offer-zoom]');
    if (zoom instanceof HTMLElement) {
      this.#openLightbox(zoom.dataset.landingOfferZoom ?? '');
      return;
    }

    if (target.closest('[data-landing-offer-lightbox-close]')) {
      this.#lightbox?.close();
      return;
    }

    const arrow = target.closest('[data-landing-offer-slide]');
    if (arrow instanceof HTMLElement) {
      this.#slide(Number(arrow.dataset.landingOfferSlide));
      return;
    }

    if (target.closest('[data-landing-offer-jump]')) {
      this.querySelector('.landing-offer__card')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      return;
    }

    if (target.closest('[data-landing-offer-submit-alias]')) {
      this.#order();
      return;
    }

    const step = target.closest('[data-landing-offer-step]');
    if (step instanceof HTMLElement) {
      event.preventDefault();

      const stepRow = step.closest('[data-landing-offer-item]');
      const input = /** @type {HTMLInputElement | null} */ (stepRow?.querySelector('[data-landing-offer-qty]'));
      if (!input) return;

      const next = Number(input.value || 1) + Number(step.dataset.landingOfferStep);
      input.value = String(Math.max(1, Math.min(next, Number(input.max) || 99)));

      // Reaching for the stepper means the shopper wants this one.
      const check = /** @type {HTMLInputElement | null} */ (stepRow?.querySelector('[data-landing-offer-check]'));
      if (check && !check.checked) check.checked = true;

      this.#render();
      return;
    }

    // An option that is already the checked one fires no change event when it
    // is clicked, so a row whose first option is the default - which is every
    // row - could never be picked by tapping that option. The click is signal
    // enough on its own.
    const option = target.closest('.landing-offer__variant');
    if (option) {
      const input = option.querySelector('.landing-offer__variant-input');
      if (input instanceof HTMLInputElement && input.disabled) return;

      const rowCheck = /** @type {HTMLInputElement | null} */ (
        option.closest('[data-landing-offer-item]')?.querySelector('[data-landing-offer-check]') ?? null
      );
      if (rowCheck && !rowCheck.disabled && !rowCheck.checked) {
        rowCheck.checked = true;
        this.#render();
      }
      return;
    }

    // Anywhere else on a row picks it. The row used to be one big label, but it
    // now holds labels of its own - the tick and each option - and labels may
    // not nest, so the toggle moved here. Clicks that land on a control are
    // left alone, otherwise the browser's own toggle would be undone a moment
    // after it happened.
    const row = target.closest('[data-landing-offer-item]');
    if (!row || target.closest('label, input, button, select, a')) return;

    const check = /** @type {HTMLInputElement | null} */ (row.querySelector('[data-landing-offer-check]'));
    if (!check || check.disabled) return;

    check.checked = !check.checked;
    this.#render();
  };

  get #lightbox() {
    return /** @type {HTMLDialogElement | null} */ (this.querySelector('[data-landing-offer-lightbox]'));
  }

  /**
   * Shows an image full size, the way the reference's lightbox does. A native
   * dialog takes the top layer, traps focus and answers Escape on its own, so
   * there is no library to load for it.
   * @param {string} src
   */
  #openLightbox(src) {
    const dialog = this.#lightbox;
    const image = dialog?.querySelector('[data-landing-offer-lightbox-image]');
    if (!dialog || !(image instanceof HTMLImageElement) || !src) return;

    image.src = src;
    dialog.showModal();
  }

  /**
   * Walks the image strip one slide at a time. The step is measured from the
   * first two items rather than assumed, so it keeps pace with whatever width
   * the breakpoint gives them.
   * @param {number} direction
   */
  #slide(direction) {
    const track = this.querySelector('[data-landing-offer-track]');
    if (!(track instanceof HTMLElement)) return;

    const [first, second] = track.children;
    const step = second
      ? second.getBoundingClientRect().left - first.getBoundingClientRect().left
      : (first?.getBoundingClientRect().width ?? track.clientWidth);

    track.scrollBy({ left: step * direction, behavior: 'smooth' });
  }

  /** Greys out an arrow once the strip has nowhere further to go that way. */
  #syncArrows() {
    const track = this.querySelector('[data-landing-offer-track]');
    if (!(track instanceof HTMLElement)) return;

    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 1;
    this.querySelector('[data-landing-offer-slide="-1"]')?.toggleAttribute('disabled', atStart);
    this.querySelector('[data-landing-offer-slide="1"]')?.toggleAttribute('disabled', atEnd);
  }

  /** The ticked rows, as cart line items, each at its chosen option. */
  #selection() {
    return this.#rows
      .map((row) => ({
        row,
        check: /** @type {HTMLInputElement | null} */ (row.querySelector('[data-landing-offer-check]')),
        option: /** @type {HTMLInputElement | null} */ (row.querySelector('[data-landing-offer-variant]:checked')),
        qty: /** @type {HTMLInputElement | null} */ (row.querySelector('[data-landing-offer-qty]')),
      }))
      .filter(({ check, option }) => check?.checked && option)
      .map(({ option, qty }) => ({
        id: Number(option?.dataset.variantId),
        quantity: Math.max(1, Number(qty?.value || 1)),
        price: Number(option?.dataset.price),
        available: option?.dataset.available === 'true',
      }));
  }

  /** Keeps the ticks and the selection line in step. */
  #render() {
    const selection = this.#selection();

    if (selection.length) {
      // Emptying the cart is the slowest thing the order does that does not
      // depend on anything the shopper has yet to decide, so it starts with the
      // first tick rather than after they press confirm. It belongs here rather
      // than on the change event because three of the tick paths set the box in
      // code and fire no event. A failure is left to the order itself, which
      // tries again and stops if it cannot. Nothing else on this page writes to
      // the cart - there is no header, no footer and nowhere to click away to -
      // so there is nothing for it to race with.
      this.#emptyCart().catch(() => {});
    }
    const count = selection.reduce((sum, line) => sum + line.quantity, 0);
    const subtotal = selection.reduce((sum, line) => sum + line.price * line.quantity, 0);

    // The template comes from the section so the shop can reword it. Delivery
    // stays out of this figure: EasySell's form settles the area and shows what
    // it adds, and a second number here would only disagree with it.
    const line = (this.dataset.summaryText || '{count} ({amount})')
      .replace('{count}', String(count))
      .replace('{amount}', String(Math.round(subtotal / 100)));

    for (const box of this.querySelectorAll('[data-landing-offer-summary]')) {
      box.textContent = line;
      box.toggleAttribute('hidden', !count);
    }

    for (const row of this.#rows) {
      const check = /** @type {HTMLInputElement | null} */ (row.querySelector('[data-landing-offer-check]'));
      row.toggleAttribute('data-selected', Boolean(check?.checked));

      // A sold out row shows words rather than a figure, so it is left alone.
      if (row.hasAttribute('data-sold-out')) continue;

      const option = row.querySelector('[data-landing-offer-variant]:checked');
      const price = row.querySelector('[data-landing-offer-price]');
      if (option instanceof HTMLInputElement && price && option.dataset.priceText) {
        price.textContent = option.dataset.priceText;
      }
    }
  }

  /**
   * Empties the cart, at most once per order. EasySell submits whatever the
   * cart holds, so anything left there from another visit would join this order
   * without the shopper ever seeing it.
   */
  #emptyCart() {
    this.#cartCleared ??= fetch(`${root()}cart/clear.js`, { method: 'POST' }).then(
      (response) => {
        if (!response.ok) throw new Error(`Cart clear returned ${response.status}`);
      },
      (error) => {
        // Forgotten right away so the order path can try again, and rethrown so
        // an order that reaches the button never adds to a cart it failed to
        // empty.
        this.#cartCleared = null;
        throw error;
      }
    );
    return this.#cartCleared;
  }

  /**
   * Raises the travelling confirm bar on a phone whenever the real button has
   * left the screen, and lowers it again when it comes back. The bar is a copy
   * of the same button, so while it is down it is taken out of the tab order
   * and hidden from screen readers.
   */
  #watchSubmit() {
    const button = this.querySelector('.landing-offer__checkout [data-landing-offer-submit-alias]');
    const bar = this.querySelector('[data-landing-offer-sticky]');
    if (!button || !bar || typeof IntersectionObserver !== 'function') return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const show = !entry.isIntersecting;
        this.toggleAttribute('data-sticky-cta', show);
        // The floating message button watches this attribute across the page
        // and lifts itself clear of whatever bar is showing.
        if (show) bar.setAttribute('data-stuck', 'true');
        else bar.removeAttribute('data-stuck');
        bar.toggleAttribute('aria-hidden', !show);
        bar.querySelector('button')?.setAttribute('tabindex', show ? '0' : '-1');
      },
      { rootMargin: '0px 0px -8px 0px' }
    );
    observer.observe(button);
  }

  /** @param {string} message */
  #showError(message) {
    const box = this.querySelector('[data-landing-offer-error]');
    if (!box) return;
    box.textContent = message;
    box.toggleAttribute('hidden', !message);
  }

  /**
   * Loads the cart with the selection and opens EasySell's form, which asks for
   * the name, phone, address and area and sends the order itself.
   */
  async #order() {
    // Touch and mouse are already off while an order is in flight, but the
    // button keeps its keyboard focus, so Enter would start a second one.
    if (this.hasAttribute('data-ordering')) return;

    const selection = this.#selection().filter((line) => line.available);

    if (!selection.length) {
      this.#showError(this.dataset.errorNoItems || 'Please choose at least one product.');
      this.querySelector('.landing-offer__grid')?.scrollIntoView({ behavior: 'smooth', block: 'center' });
      return;
    }

    this.#showError('');
    this.toggleAttribute('data-ordering', true);

    try {
      // Usually finished long ago, started with the shopper's first tick, and
      // it throws if the cart could not be emptied. Retired straight after, so
      // a second order gets its own.
      await this.#emptyCart();
      this.#cartCleared = null;

      const response = await fetch(`${root()}cart/add.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: selection.map(({ id, quantity }) => ({ id, quantity })) }),
      });
      if (!response.ok) throw new Error(`Cart returned ${response.status}`);

      if (typeof window.openTysloForm !== 'function') {
        // The COD app is not on the page at all. The cart is already correct,
        // so checkout is the honest fallback.
        window.location.href = `${root()}checkout`;
        return;
      }

      await window.openTysloForm(true);
    } catch (error) {
      console.error('Landing offer order failed:', error);
      this.#showError(this.dataset.errorGeneric || 'Please try again.');
    } finally {
      this.toggleAttribute('data-ordering', false);
    }
  }
}

customElements.define('landing-offer', LandingOffer);
